/*
 * Copyright 2025 Intuition Robotics ltd
 */
import * as React from "react";
import {BaseComponent, Dialog_Builder, DialogModule, ToastModule, TS_Input} from "@intuitionrobotics/thunderstorm/frontend";
import {deepClone, generateHex} from "@intuitionrobotics/ts-common";
import {inputTextField, Spacing} from "@styles/styles";
import {DialogCard} from "../../widget/DialogCard";
import {AppRoleLevel, DB_Permission, ID_ALL, OrgRoleLevel, PermissionLevel} from "@app/app-shared/permission";
import {AppPermissionsModule} from "@modules/AppPermissionsModule";
import {OnUsersUpdated, UsersModule} from "@modules/UsersModule";
import {HGap} from "../../widget/HGap";
import {DB_User} from "@app/ir-q-app-common/types/care-center/user";
import {HLine} from "../../widget/HLine";
import {SearchBar} from "../SearchBar";
import {OKButton} from "../../widget/OKButton";
import {COLORS} from "@res/colors";
import {FONTS} from "@res/fonts";
import {Checkbox} from "../patients/Checkbox";
import {ORGANIZATION_TYPE} from "@app/ir-q-app-common/types/care-center/organization";
import {ICONS} from "@res/icons";
import {MultiSelectDropDown, MultiSelectOption} from "../../../../widgets/MultiSelectDropdown";
import {OrganizationsContext, OrganizationsContextType} from "../organizations/OrganizationsContext";

type State = {
    loading: boolean
    permissionName?: string,
    orgRoles: PermissionLevel
    appRoles: AppRoleLevel[]
    userIds: string[],
    searchText: string,
    showUsers: boolean,
    showExtra: boolean,
}
type Props = {
    existingPermission?: DB_Permission;
    userId?: string;
    upsertPermission: (permissions: DB_Permission) => void
    permissions: DB_Permission[]
}

const groupUsers = (users: DB_User[]): DB_User[][] => {
    if (!users)
        return [];
    const groupCount = 2;
    const groups = [];
    const groupSize = Math.max(Math.ceil(users.length / groupCount), 11);
    for (let i = 0; i < groupCount; i++) {
        const startIndex = i * groupSize;
        const endIndex = startIndex + groupSize;
        const column: DB_User[] = users.slice(startIndex, endIndex);
        groups.push(column);
    }
    return groups
};

export class PermissionDialog
    extends BaseComponent<Props, State>
    implements OnUsersUpdated {

    __onUsersUpdated() {
        this.forceUpdate();
    }

    public static show(permissions: DB_Permission[], upsertPermission: (permissions: DB_Permission) => void, permission?: DB_Permission, userId?: string) {
        new Dialog_Builder(
            <PermissionDialog
                permissions={permissions}
                existingPermission={permission ? deepClone(permission) : undefined}
                userId={userId}
                upsertPermission={upsertPermission}
            />
        ).setAllowIndirectClosing(true)
            .show();
    }

    constructor(props: Props) {
        super(props);

        const existingPermission = this.props.existingPermission;
        let userIds = existingPermission ? UsersModule.getUsers().filter(u => u.permissionId === existingPermission.id).map(u => u._id) : [];
        if (this.props.userId && !userIds?.length)
            userIds = [...userIds, this.props.userId];
        const orgRoles = props.existingPermission?.orgRoles || {};
        this.state = {
            loading: false,
            searchText: '',
            permissionName: props.existingPermission?.name,
            orgRoles: orgRoles,
            appRoles: props.existingPermission?.appRoles || [],
            userIds,
            showUsers: false,
            showExtra: Object.keys(orgRoles).length === 0
        };
    }

    private submit = () => {
        if (!this.state.orgRoles || !this.state.permissionName || !this.state.appRoles)
            return;

        const permission = {
            name: this.state.permissionName,
            id: this.props.existingPermission?.id,
            orgRoles: this.state.orgRoles,
            appRoles: this.state.appRoles
        };
        AppPermissionsModule.setPermission(permission, this.state.userIds).then(id => {
            this.props.upsertPermission({...permission, id})
            UsersModule.query();
            DialogModule.close();
        }).catch(e => {
            console.error("Failed to save permission", e);
            this.setState({loading: false});
            ToastModule.toastError("Failed to save permission");
        });
    };

    render() {
        return <OrganizationsContext.Consumer>
            {orgCtx => <DialogCard
                showX={true}
                loading={this.state.loading}
                content={this.renderContent(orgCtx)}
                saveLabel={`${this.props.existingPermission ? "Update" : "Add"} permission`}
                cancelLabel={"Cancel"}
                title={`${this.props.existingPermission ? "Update" : "Add"} permission`}
                onSave={() => {
                    this.setState({loading: true}, () => this.submit());
                    // Keep dialog open.
                    return true;
                }}
                saveEnabled={!!this.state.permissionName}
            />}
        </OrganizationsContext.Consumer>
    }

    private renderContent = (orgCtx: OrganizationsContextType) => {
        return <div className={`ll_v_l fill border-box`}>
            <div className={`permissions-body  ll_v_c match_height`}>
                <div className={`ll_v_c match_height`}>
                    <div className={`margin-bottom-15 ll_v_l`}>
                        <div className={"margin-bottom-5"}>
                            Name:
                        </div>
                        <div>
                            <TS_Input<string>
                                className={inputTextField}
                                id={"user-mail"}
                                value={this.state.permissionName || ""}
                                placeholder={"Name"}
                                onChange={(permissionName: string) => this.setState({permissionName})}
                                type={"text"}
                                name={generateHex(8)}
                                spellCheck={true}/>
                        </div>
                    </div>
                    {this.renderAppPermissions()}
                    {this.renderOrgPermissions(orgCtx)}
                    <HLine style={{marginTop: Spacing.m}}/>
                    {this.renderUserSelection()}
                </div>
            </div>
        </div>;
    };

    private renderUserSelection = () => {
        if (!this.state.showUsers) {
            return <div style={{marginTop: 16}}>
                <OKButton
                    text={'Assign to users'}
                    textStyle={{whiteSpace: 'nowrap'}}
                    enabled={true}
                    onClick={() => this.setState({showUsers: true})}
                />
            </div>
        }
        const users = UsersModule.getUsers();
        const groups = groupUsers(users.filter(u => {
            if (!this.state.searchText)
                return true;

            const fieldsToSearch = [u.mail, u.first, u.last, u.mail, u.title];
            for (const field of fieldsToSearch) {
                if (field?.toLowerCase().includes(this.state.searchText.toLowerCase()))
                    return true;
            }
            return false;
        }));
        return <div style={{display: "flex", flexDirection: "column", marginTop: 16}}>
            <div style={{display: "flex", alignItems: "center"}}>
                <SearchBar value={this.state.searchText} onValueChange={(value) => this.setState({searchText: value})}
                           onClose={() => this.setState({searchText: ''})} placeholder={"Search by name"}/>
                <div style={{marginLeft: 32}}>
                    <OKButton
                        text={'Hide users'}
                        textStyle={{whiteSpace: 'nowrap'}}
                        enabled={true}
                        onClick={() => this.setState({showUsers: false})}
                    />
                </div>
            </div>
            <div className={'ll_h_s'} style={{maxHeight: 200, overflowY: 'auto', marginTop: 20}}>
                {groups.map((chunk, idx) => <div key={idx} className={'ll_v_s'} style={{width: 300}}>
                    {chunk.map(user => {
                        const id = user._id;
                        const isSelected = this.state.userIds.includes(id);
                        let text = user.mail;
                        const userPermission = this.props.permissions.find(p => user.permissionId === p.id);
                        if (userPermission && (!this.props.existingPermission || (userPermission.id !== this.props.existingPermission.id))) {
                            text += ` (${userPermission.name})`;
                        }
                        return <div key={user._id} className={'ll_h_c'} style={{minHeight: 34}}>
                            <Checkbox on={isSelected} onChange={on => {
                                this.setState(s => ({userIds: on ? [...s.userIds, id] : s.userIds.filter(it => it !== id)}));
                            }}/>
                            <HGap h={5}/>
                            <div style={{fontSize: 16, color: "#303030"}}>{text}</div>
                        </div>;
                    })}
                </div>)}
            </div>
        </div>
    };

    private renderOrgPermissions(orgCtx: OrganizationsContextType) {
        const orgRoles = this.state.orgRoles;
        const orgs = Object.values(orgCtx.organizations);
        const shouldShowAddMore = !orgRoles[ID_ALL] && orgs.length - Object.keys(orgRoles).length > 0 && !this.state.showExtra;
        const getOptions = (orgId?: string) => {
            const entries = Object.entries(orgRoles);
            // If I have one I want to be able to move it to all but if I have one then the extra I dont want to be able to move it to all
            const initialValue = entries.length === 0 || (entries.length === 1 && orgId) ? [{value: ID_ALL, label: "All", checked: !!orgRoles[ID_ALL]}] : [];
            return orgs.reduce((acc: MultiSelectOption[], o) => {
                const isThisOrg = o._id === orgId;
                if (!isThisOrg && orgRoles[o._id])
                    return acc;

                return [...acc, {value: o._id, label: o.name, checked: isThisOrg && !!orgRoles[o._id]}];
            }, initialValue);
        };
        return <div className={"flex_center permissions-row"}>
            {Object.entries(orgRoles).map(([orgId, levels]) => {
                if (!levels)
                    return;

                const options: MultiSelectOption[] = getOptions(orgId);
                return <RenderOrgPermissionsSelect
                    onLevelSelected={(_id) => {
                        this.setState(s => ({
                            ...s,
                            orgRoles: {
                                ...s.orgRoles,
                                [orgId]: levels.includes(_id) ? levels.filter(r => r !== _id) : [...levels, _id]
                            }
                        }))
                    }}
                    onOrgSelected={(id) => {
                        if (id === orgId)
                            return;

                        this.setState(s => {
                            const roles = s.orgRoles;
                            delete roles[orgId];
                            return {...s, orgRoles: {...roles, [id]: []}};
                        });
                    }}
                    levels={levels}
                    options={options}
                    _key={`option_extra`}
                    key={"permission_row_" + orgId}
                    orgId={orgId}
                    onDelete={() => {
                        this.setState(s => {
                            const roles = s.orgRoles;
                            delete roles[orgId];
                            return {...s, orgRoles: roles};
                        })
                    }}
                />
            })}
            {this.state.showExtra && <RenderOrgPermissionsSelect
                onOrgSelected={(id) => {
                    this.setState(s => ({
                        ...s,
                        orgRoles: {
                            ...s.orgRoles,
                            [id]: []
                        },
                        showExtra: false
                    }))
                }}
                options={getOptions()}
                _key={`option_extra`}
                key={`option_extra`}
                onDelete={() => this.setState({showExtra: false})}
            />}
            {shouldShowAddMore && <div onClick={() => this.setState({showExtra: true})} style={{marginTop: 5, justifySelf: "end"}}>
                {FONTS.reg_11(`+ Add more`, {cursor: 'pointer'})}
            </div>}
        </div>
    }

    private renderAppPermissions() {
        const appRoles = Object.entries(AppRoleLevel).map(([k, r]): MultiSelectOption => ({
            value: r,
            label: r,
            checked: this.state.appRoles.includes(r)
        }));
        return <div className={"ll_v_c justify-center"}>
            <div>
                App Roles:
            </div>
            <MultiSelectDropDown
                options={appRoles}
                onItemClick={(_id) => {
                    this.setState(s => {
                        const id = _id as AppRoleLevel;
                        return {appRoles: s.appRoles.includes(id) ? s.appRoles.filter(r => r !== id) : [...s.appRoles, id]};
                    })
                }}
                labels={{
                    noSelection: "Select one"
                }}
            />
        </div>
    }
}

const getAvailableRolesByOrganization = (orgCtx: OrganizationsContextType, orgId?: string, levels?: OrgRoleLevel[]): MultiSelectOption[] => {
    if (!orgId)
        return [];

    let isExtMsgChecked = false;
    let isClinicalChecked = false;
    let isClMsgChecked = false;
    let isCSMsgChecked = false;
    let isRemindersChecked = false;
    if (levels) {
        for (const level of levels) {
            switch (level) {
                case OrgRoleLevel.USER_DATA:
                    isClinicalChecked = true;
                    break;
                case OrgRoleLevel.CS_MESSAGE:
                    isCSMsgChecked = true;
                    break;
                case OrgRoleLevel.CL_MESSAGE:
                    isClMsgChecked = true;
                    break;
                case OrgRoleLevel.EX_MESSAGE:
                    isExtMsgChecked = true;
                    break;
                case OrgRoleLevel.REMINDERS:
                    isRemindersChecked = true;
            }
        }
    }

    const baseRoles = [
        {label: "User Data", value: OrgRoleLevel.USER_DATA, checked: isClinicalChecked},
        {label: "Customer Service Messages", value: OrgRoleLevel.CS_MESSAGE, checked: isCSMsgChecked}
    ]

    const remindersRole = {label: "Reminders", value: OrgRoleLevel.REMINDERS, checked: isRemindersChecked};
    const clinicalMessagesRole = {label: "Clinical Messages", value: OrgRoleLevel.CL_MESSAGE, checked: isClMsgChecked};
    const externalMessagesRole = {label: "External Messages", value: OrgRoleLevel.EX_MESSAGE, checked: isExtMsgChecked};

    if (orgId === ID_ALL) {
        return [
            ...baseRoles,
            clinicalMessagesRole,
            externalMessagesRole,
            remindersRole
        ]
    }
    const org = orgCtx.organizations[orgId];
    if (!org)
        return [];

    switch (org.type) {
        case ORGANIZATION_TYPE.INTERNAL:
            return [
                ...baseRoles,
                remindersRole
            ]
        case ORGANIZATION_TYPE.CLINICAL:
            return [
                ...baseRoles,
                clinicalMessagesRole,
                remindersRole
            ]
        case ORGANIZATION_TYPE.EXTERNAL:
            return [
                ...baseRoles,
                externalMessagesRole,
                remindersRole
            ]
        case ORGANIZATION_TYPE.MULTI_CONTACT:
            return [
                ...baseRoles,
                clinicalMessagesRole,
                externalMessagesRole,
                remindersRole
            ]
    }
};


type P = {
    levels?: OrgRoleLevel[]
    options: MultiSelectOption[]
    _key: string
    orgId?: string
    onDelete: () => void
    onOrgSelected: (orgId: string) => void
    onLevelSelected?: (level: OrgRoleLevel) => void
};
const RenderOrgPermissionsSelect = ({options, orgId, _key, levels, onDelete, onOrgSelected, onLevelSelected}: P) => {
    return <OrganizationsContext.Consumer>
        {orgCtx => <div key={_key} style={{display: "flex"}}>
            <div className={"ll_h_c"}>
                <MultiSelectDropDown
                    options={options}
                    onItemClick={(_id) => {
                        onOrgSelected(_id);
                    }}
                    labels={{
                        noSelection: "Select one"
                    }}
                />
                <div className={"ll_h_c"}>
                    <MultiSelectDropDown
                        options={getAvailableRolesByOrganization(orgCtx, orgId, levels)}
                        onItemClick={(_id) => {
                            onLevelSelected?.(_id as OrgRoleLevel);
                        }}
                        labels={{
                            noOptions: "Select an org first",
                            noSelection: "No options selected"
                        }}
                    />
                </div>
            </div>
            <div style={{marginLeft: 15, cursor: 'pointer', alignSelf: "center"}} onClick={() => onDelete()}>
                {ICONS.x(COLORS.red, 20)}
            </div>
        </div>}
    </OrganizationsContext.Consumer>

}
